import React from "react";
import { EmptyState, FlagList, SectionGrid, SummaryGrid } from "../AdminAnalyticsCards";
import CssBarChart from "../charts/CssBarChart";
import AdminAnalyticsTable, { UserCell } from "../tables/AdminAnalyticsTable";
import { aa, asArray, asNumber, formatDateTime, formatDay, formatDuration, formatNumber, formatRate, getUserLabel, getUserSubLabel, pickValue } from "../adminAnalyticsUtils";
function hasOverviewData(payload) {
  const summary = payload?.summary || {};
  return asNumber(summary.newRegistrations) > 0 || asNumber(summary.activeUsers) > 0 || asNumber(summary.totalSessions) > 0 || asArray(payload?.topFeatureAreas).length > 0 || asArray(payload?.topEventNames).length > 0 || asArray(payload?.recentNewUserBehavior).length > 0;
}
function behaviorFlags(row) {
  return [{
    label: aa("feed", "Feed"),
    active: row?.openedFeed
  }, {
    label: aa("map", "Map"),
    active: row?.openedMap
  }, {
    label: aa("fishipedia", "Fishipedia"),
    active: row?.openedFishipedia
  }, {
    label: aa("create_opened", "Create opened"),
    active: row?.openedCreatePost
  }, {
    label: aa("create_submitted", "Create submitted"),
    active: row?.submittedCreatePost
  }];
}
function formatUsersSublabel(count) {
  return aa("users_sublabel", "{{count}} users", {
    count: formatNumber(count),
  });
}
function formatFeatureSublabel(feature) {
  return aa("feature_sublabel", "{{feature}} feature", {
    feature: feature || aa("unknown_2", "unknown"),
  });
}
export default function AdminAnalyticsOverviewTab({
  data
}) {
  const summary = data?.summary || {};
  const cards = [{
    label: aa("new_registrations", "New registrations"),
    value: formatNumber(summary.newRegistrations)
  }, {
    label: aa("active_users", "Active users"),
    value: formatNumber(summary.activeUsers)
  }, {
    label: aa("total_sessions", "Total sessions"),
    value: formatNumber(summary.totalSessions)
  }, {
    label: aa("avg_session_duration", "Avg session duration"),
    value: formatDuration(summary.averageSessionDurationSeconds)
  }, {
    label: aa("returning_users", "Returning users"),
    value: formatNumber(summary.returningUsers)
  }, {
    label: aa("feed_only_users", "Feed-only users"),
    value: formatNumber(summary.feedOnlyUsers)
  }, {
    label: aa("create_post_opened", "Create Post opened"),
    value: formatNumber(summary.createPostOpened)
  }, {
    label: aa("create_post_submitted", "Create Post submitted"),
    value: formatNumber(summary.createPostSubmitted)
  }, {
    label: aa("create_post_submit_rate", "Create Post submit rate"),
    value: formatRate(summary.createPostSubmitRate)
  }];
  const createRows = [{
    label: aa("opened", "Opened"),
    value: summary.createPostOpened
  }, {
    label: aa("submitted", "Submitted"),
    value: summary.createPostSubmitted
  }];
  return <>
      {!hasOverviewData(data) ? <EmptyState title={aa("no_analytics_data_yet", "No analytics data yet")} body={aa("this_period_does_not_have_user_analytics_activity_to_display", "This period does not have user analytics activity to display.")} /> : null}

      <SummaryGrid cards={cards} />

      <SectionGrid>
        <CssBarChart title={aa("create_post_funnel", "Create Post funnel")} value={formatRate(summary.createPostSubmitRate)} rows={createRows} getLabel={row => row.label} getValue={row => row.value} className="admin-analytics-funnel" />

        <CssBarChart title={aa("registrations_by_day", "Registrations by day")} rows={data?.registrationsByDay} getLabel={row => formatDay(row?.day)} getValue={row => pickValue(row, ["registrations", "users"])} />

        <CssBarChart title={aa("sessions_by_day", "Sessions by day")} rows={data?.sessionsByDay} getLabel={row => formatDay(row?.day)} getValue={row => row?.sessions} />

        <CssBarChart title={aa("average_session_duration_by_day", "Average session duration by day")} rows={data?.averageSessionDurationByDay} getLabel={row => formatDay(row?.day)} getValue={row => pickValue(row, ["averageSessionDurationSeconds", "averageSeconds"])} formatter={formatDuration} />

        <CssBarChart title={aa("top_feature_areas", "Top feature areas")} rows={data?.topFeatureAreas} getLabel={row => row?.featureArea || aa("unknown", "Unknown")} getValue={row => row?.eventCount} getSublabel={row => formatUsersSublabel(row?.uniqueUsers)} />

        <CssBarChart title={aa("top_event_names", "Top event names")} rows={data?.topEventNames} getLabel={row => row?.eventName || aa("unknown", "Unknown")} getValue={row => row?.eventCount} getSublabel={row => formatFeatureSublabel(row?.featureArea)} />

        <AdminAnalyticsTable title={aa("return_count_per_user", "Return count per user")} rows={data?.returnCounts} rowKey={row => getUserSubLabel(row)} columns={[{
        header: aa("user", "User"),
        render: row => <UserCell title={getUserLabel(row)} subtitle={getUserSubLabel(row)} />
      }, {
        header: aa("sessions", "Sessions"),
        render: row => formatNumber(row?.sessionCount)
      }, {
        header: aa("returns", "Returns"),
        render: row => formatNumber(row?.returnCount)
      }, {
        header: aa("last_session", "Last session"),
        render: row => formatDateTime(row?.lastSessionAt)
      }]} />

        <AdminAnalyticsTable title={aa("recent_new_user_behavior", "Recent new-user behavior")} rows={data?.recentNewUserBehavior} rowKey={row => getUserSubLabel(row)} className="admin-analytics-table-panel--wide" minWidth={860} columns={[{
        header: aa("user", "User"),
        render: row => <UserCell title={getUserLabel(row)} subtitle={getUserSubLabel(row)} />
      }, {
        header: aa("created", "Created"),
        render: row => formatDateTime(row?.createdAt)
      }, {
        header: aa("events", "Events"),
        render: row => formatNumber(row?.eventCount)
      }, {
        header: aa("sessions", "Sessions"),
        render: row => formatNumber(row?.sessionCount)
      }, {
        header: aa("features", "Features"),
        render: row => asArray(row?.featuresUsed).join(", ") || aa("none", "None")
      }, {
        header: aa("flags", "Flags"),
        render: row => <FlagList flags={behaviorFlags(row)} />
      }]} />

        <AdminAnalyticsTable title={aa("users_with_no_meaningful_activity", "Users with no meaningful activity")} rows={data?.usersWithNoMeaningfulActivity} rowKey={row => getUserSubLabel(row)} className="admin-analytics-table-panel--wide" columns={[{
        header: aa("user", "User"),
        render: row => <UserCell title={getUserLabel(row)} subtitle={getUserSubLabel(row)} />
      }, {
        header: aa("created", "Created"),
        render: row => formatDateTime(row?.createdAt)
      }, {
        header: aa("profile_status", "Profile status"),
        render: row => row?.profileStatus || aa("unknown_2", "unknown")
      }, {
        header: aa("events", "Events"),
        render: row => formatNumber(row?.eventCount)
      }, {
        header: aa("sessions", "Sessions"),
        render: row => formatNumber(row?.sessionCount)
      }]} />

      </SectionGrid>
    </>;
}
